import type { PropsWithChildren } from 'react';
import React, { useMemo } from 'react';

// MUI Components
import Container from '@mui/material/Container';
import Grid from '@mui/material/Grid';

// Hooks
import { useWindowSize } from 'react-use';

import Box from '@mui/material/Box';

import { useTranslation } from 'next-i18next';

import useAppRouter from 'src/hooks/useAppRouter';
import { useCompanyProfileQuery } from 'src/hooks/__generated__/queries';
import type { Company } from 'src/types/__generated__/graphql';
import { CompanyLabel } from 'src/types/__generated__/graphql';
import useDynamicLogEvents, { AnalyticsContextProvider } from 'src/hooks/useDynamicLogEvents';

// Components
import CompanyDetailsBlock from 'src/components/company/CompanyDetailsBlock';
import ImagesScroller from 'src/components/ui/ImagesScroller';
import Typography from 'src/components/ui/Typography';
import CompanyCommonRelations from 'src/components/company/CompanyCommonRelations';
import CompanyHeader from 'src/components/company/CompanyHeader';
import CompanyHeaderCTAs from 'src/components/company/CompanyHeaderCTAs';
import CompanyMembersSection from 'src/components/company/CompanyMembersSection';
import CompanyRecommendations from 'src/components/company/CompanyRecommendations';
import FullWidthLayout from 'src/components/other/FullWidthLayout';
import ProfileTopBar from 'src/components/profile/ProfileTopBar';
import { Tab, Tabs } from 'src/components/ui/Tabs';
import { isCompanyLabelAllowed } from 'src/utils/companyUtils';

// Constants
import { breakpoints } from 'src/constants/breakpoints';
import { routePathCompany } from 'src/constants/router';
import { colours } from 'src/constants/colours';
import { styledComponent } from 'src/utils/styled';
import { AnalyticsEvent, getUserOrCompanyOrPagesJaunesProLogParams } from 'src/utils/analytics';

const Mission = styledComponent(Typography)`
    span {
        color: ${colours.secondary.main};
    }
`;

enum TabTypes {
    home = 'Home',
    info = 'Info',
    team = 'Team',
    showcase = 'Showcase',
    activity = 'Activity',
    jobs = 'Jobs',
}

const CompanyProfileLayout: React.FC<PropsWithChildren> = ({ children }) => {
    const { t } = useTranslation(['common', 'company']);
    const router = useAppRouter();
    const { width } = useWindowSize();

    const { logEventDynamic } = useDynamicLogEvents();

    const { isMobile, isDesktopLarge } = useMemo(
        () => ({
            isMobile: width < breakpoints.tabletPortrait,
            isDesktopLarge: width >= breakpoints.desktopLarge,
        }),
        [width],
    );

    const companyId = router.query.companyId as string;
    const { data } = useCompanyProfileQuery({
        variables: {
            companyId: companyId as string,
            showcaseLimit: null,
        },
        skip: !companyId,
    });
    const analyticsValue = useMemo(
        () =>
            getUserOrCompanyOrPagesJaunesProLogParams({
                company: { id: companyId, name: data?.companyProfile.name as string },
            }),
        [data?.companyProfile.name, companyId],
    );

    const currentTab = router.asPath.split('#')[0].split('/')[3]?.split('?')[0] ?? 'home';

    const handleTabChange = async (_ev: React.SyntheticEvent, newTab: TabTypes) => {
        const tabAnalyticsEvent = (newTab.charAt(0).toUpperCase() + newTab.slice(1)) as keyof typeof AnalyticsEvent;
        // cannot rely on analyticsValue as it is made available inside return
        logEventDynamic(
            AnalyticsEvent[tabAnalyticsEvent],
            getUserOrCompanyOrPagesJaunesProLogParams({
                company: { id: companyId, name: data?.companyProfile.name as string },
            }),
        );
        let url = `${routePathCompany}/${companyId}/${newTab}`;
        if (newTab === TabTypes.home.toLowerCase()) {
            url = `${routePathCompany}/${companyId}`;
        }
        await router.push(url, url, {
            scroll: false,
        });
    };

    const companyGroup = isCompanyLabelAllowed(CompanyLabel.Group, data?.companyProfile);
    const imagesUrls = data?.companyProfile.showcase
        ? data.companyProfile.showcase.filter((i) => Boolean(i?.url)).map((i) => String(i?.url))
        : undefined;

    const CtaComponent = data?.companyProfile && <CompanyHeaderCTAs company={data.companyProfile} />;

    return (
        <AnalyticsContextProvider value={analyticsValue}>
            <FullWidthLayout>
                <Container>
                    <ProfileTopBar />
                </Container>
                <Box>
                    {isMobile && (
                        <CompanyHeader
                            company={data?.companyProfile as unknown as Company}
                            ctaComponent={CtaComponent}
                        />
                    )}
                </Box>
                <Container>
                    <Grid spacing={3} container>
                        <Grid xs={12} sm={12} md={12} lg={12} xl={8} item>
                            {!isMobile && <CompanyHeader ctaComponent={CtaComponent} />}
                            {!isDesktopLarge && data?.companyProfile && (
                                <Grid spacing={isMobile ? 1 : 2} container>
                                    <Grid xs={12} sm={6} md={6} lg={6} item>
                                        <CompanyCommonRelations companyId={data.companyProfile.id} />
                                    </Grid>
                                    <Grid xs={12} sm={6} md={6} lg={6} item>
                                        <CompanyRecommendations company={data.companyProfile} />
                                    </Grid>
                                </Grid>
                            )}
                            <Box mt={isDesktopLarge ? 0 : 3}>
                                <Tabs
                                    value={currentTab}
                                    onChange={handleTabChange}
                                    variant={isMobile ? 'scrollable' : 'fullWidth'}
                                >
                                    <Tab
                                        data-target="company-profile-tab"
                                        data-target-id="company-profile-home"
                                        label={t('company:home')}
                                        value={TabTypes.home.toLowerCase()}
                                    />
                                    <Tab
                                        data-target="company-profile-tab"
                                        data-target-id="company-profile-info"
                                        label={t('company:info')}
                                        value={TabTypes.info.toLowerCase()}
                                    />
                                    <Tab
                                        data-target="company-profile-tab"
                                        data-target-id="company-profile-team"
                                        label={companyGroup ? t('company:members') : t('company:team')}
                                        value={TabTypes.team.toLowerCase()}
                                    />
                                    <Tab
                                        data-target="company-profile-tab"
                                        data-target-id="company-profile-showcase"
                                        label={t('company:showcase')}
                                        value={TabTypes.showcase.toLowerCase()}
                                    />
                                    <Tab
                                        data-target="company-profile-tab"
                                        data-target-id="company-profile-activity"
                                        label={t('company:activity')}
                                        value={TabTypes.activity.toLowerCase()}
                                    />
                                    <Tab
                                        data-target="company-profile-tab"
                                        data-target-id="company-profile-jobs"
                                        label={t('company:jobs')}
                                        value={TabTypes.jobs.toLowerCase()}
                                    />
                                </Tabs>
                            </Box>
                            {(currentTab === TabTypes.home.toLowerCase() ||
                                currentTab === TabTypes.info.toLowerCase()) && (
                                <Box display="flex" mt={2} mb={2} flexDirection="column">
                                    {imagesUrls?.length ? (
                                        <Box>
                                            <ImagesScroller imagesUrls={imagesUrls} />
                                        </Box>
                                    ) : null}
                                    {data?.companyProfile.mission && (
                                        <Box
                                            mb={1}
                                            width="50%"
                                            display="flex"
                                            alignSelf="center"
                                            data-target-id="company-profile-mission"
                                            justifyContent="center"
                                        >
                                            <Mission variant="h2" textAlign="center" serif>
                                                <span>“</span>
                                                {data.companyProfile.mission}
                                                <span>”</span>
                                            </Mission>
                                        </Box>
                                    )}
                                </Box>
                            )}
                            <Box>{children}</Box>
                        </Grid>
                        {isDesktopLarge && (
                            <Grid xl={4} item>
                                {data?.companyProfile && (
                                    <Grid spacing={2} container>
                                        <Grid xl={12} item>
                                            <CompanyCommonRelations companyId={data.companyProfile.id} />
                                        </Grid>
                                        <Grid xl={12} item>
                                            <CompanyRecommendations company={data.companyProfile} />
                                        </Grid>
                                        <Grid xl={12} item>
                                            <CompanyMembersSection company={data.companyProfile} />
                                        </Grid>
                                        <Grid xl={12} item>
                                            <CompanyDetailsBlock
                                                company={data.companyProfile as unknown as Company}
                                                displayOnCard
                                            />
                                        </Grid>
                                    </Grid>
                                )}
                            </Grid>
                        )}
                    </Grid>
                </Container>
            </FullWidthLayout>
        </AnalyticsContextProvider>
    );
};

export default CompanyProfileLayout;
